/**
 * Stream an assistant turn from POST /v1/chat/sessions/{id}/messages/stream.
 * Best-effort live tokens; the durable copy arrives via the WebSocket + a
 * messages re-fetch (two-read reconnect). Uses fetch streaming (RN 0.72+).
 */
import { toRequestError } from '../utils/apiError';
import type { TargetPriceCard } from './targetPriceCard';

/** Action carried by a structured question option: routes the tap to a flow
 * entry point instead of sending the label as a chat turn. Named *Import* for
 * historical reasons — the import trio was first; `open_thesis_builder` (the
 * target-price hard-gate's refusal chip) rides the same mechanism and routes
 * to the structured build_thesis turn, and `refresh_research` (the research
 * staleness card's confirm chip) routes to the structured refresh turn. Keep
 * this in step with the backend's `OptionAction` literal (schemas/chat.py): the
 * backend re-validates a persisted chip on the read path, so a name it does not
 * recognise breaks the whole messages endpoint for that session. */
export type ImportOptionAction =
  | 'open_import_picker'
  | 'open_import_sheet'
  | 'discard_import'
  | 'open_thesis_builder'
  | 'refresh_research';

/** A tappable question option. Plain strings remain the common case
 * (back-compat); objects add an `action` for the allowlisted UI flows.
 * `symbol` is the action's target instrument (open_thesis_builder, and
 * refresh_research — where it becomes the command's `ticker`): the
 * backend sends it key-present as a string or null — a missing key and an
 * explicit null are both tolerated (the dispatch resolves null → no symbol).
 * `accept` is the target-price wizard's stage keys the tap CONFIRMS — mutually
 * exclusive with `action`: such a chip posts an accept-only edit instead of
 * sending a chat turn, which is what makes it equal to the input group's
 * Confirm button. */
export type QuestionOption = {
  label: string;
  action?: ImportOptionAction;
  symbol?: string | null;
  accept?: string[];
};

/** An active question the agent needs answered before it continues.
 * `prompt` is a one-line restatement (may be null — the reply body already
 * carries the question); `options` are the tappable short answers (plain
 * strings or {@link QuestionOption} objects). `multi` marks a pick-many step
 * (e.g. pillars, risks) where several options are valid; absent/undefined
 * means single-select. */
export interface ChatQuestion {
  prompt: string | null;
  options: (string | QuestionOption)[];
  multi?: boolean;
}

/** Target of a thesis save: an existing portfolio position or a watchlist
 * item (the watchlist item is created server-side iff needed). */
export type ThesisTarget = { kind: 'position' | 'watchlist_item'; id: number };

/** The structured `build_thesis` seeding turn (spec §3.2). Sent through the
 * existing send/stream endpoint as an optional sibling of `content`. */
export interface ThesisCommand {
  command: 'build_thesis';
  symbol?: string | null;
  target?: ThesisTarget | null;
  seed_snapshot?: Record<string, unknown> | null;
}

/** One file of an `import_positions` command turn. `file_data` is base64
 * (backend validates 1–10MB after decode); `file_name` is optional — the
 * backend derives a default per `file_type`. */
export interface ImportFilePayload {
  file_data: string;
  file_type: 'image' | 'pdf';
  file_name?: string;
}

/** The structured `import_positions` turn (spec §3.1), sent through the
 * existing send/stream endpoint as an optional sibling of `content`.
 * `origin` defaults to 'menu' backend-side. */
export interface ImportCommandPayload {
  command: 'import_positions';
  files: ImportFilePayload[];
  origin?: 'menu' | 'agent_suggest';
}

/** Kind of Feed item a `discuss_item` turn opens (revamp §6.3): briefs and
 * research reports are inbox rows, alerts and todos are open action items. */
export type DiscussItemKind = 'brief' | 'research' | 'alert' | 'todo';

/** The structured `discuss_item` turn (revamp §6.3): the user picked a Feed
 * item to discuss. The backend fetches the item server-side (404 when missing
 * or foreign) and composes the seed into the user message metadata — the
 * client never ships content. Discriminated union: the wire `item_id` is the
 * store row's own key, and its TYPE is pinned by the kind — inbox kinds
 * (brief/research) are UUID strings, action kinds (alert/todo) are numeric
 * ids — so a swapped branch cannot compile. */
export type DiscussItemCommandPayload =
  | { command: 'discuss_item'; kind: 'brief' | 'research'; item_id: string }
  | { command: 'discuss_item'; kind: 'alert' | 'todo'; item_id: number };

/** A Feed row handed to the chat as *context to discuss*, not a turn to fire
 * (revised 2026-10-09): the shell renders a pending card from `title`/`excerpt`
 * and holds `command` until the user sends their first message, which then
 * carries the discuss_item payload. This replaces the old auto-fired
 * `initialCommand` so the user always types the opening question. */
export interface DiscussSeed {
  command: DiscussItemCommandPayload;
  title: string;
  /** The row's preview / meta line — null when the item carries neither. */
  excerpt: string | null;
}

/** The structured `build_profile` turn (revamp T24): the user opened the
 * investor-profile builder from the ＋ menu. No payload — the flow's state
 * lives in the agent-emitted profile card. */
export interface BuildProfileCommandPayload {
  command: 'build_profile';
}

/** The structured `target_price` turn (content-quality spec §11, Part 2,
 * T15): open the target-price builder for a concrete symbol. Mirrors the
 * backend `TargetPriceCommand` schema (schemas/chat.py). ENTRY-POINT RULE:
 * every UI path that sends this MUST carry a symbol — a null symbol fails
 * the backend thesis hard-gate and dead-ends into the refusal turn. */
export interface TargetPriceCommandPayload {
  command: 'target_price';
  symbol?: string | null;
  target?: ThesisTarget | null;
  seed_snapshot?: Record<string, unknown> | null;
}

/** The structured `refresh_research` turn (research-freshness spec §C.4): the
 * user confirmed the app's own "research note is out of date" card, so the
 * cortex worker re-runs the research and writes the wiki. Mirrors the backend
 * `RefreshResearchCommand` schema (schemas/chat.py). `ticker` is required — the
 * chip label does not name the symbol, and the turn's coverage read keys on it. */
export interface RefreshResearchCommandPayload {
  command: 'refresh_research';
  ticker: string;
}

/** One row of the import review sheet (spec §3.2/§4.5). `suggestion` is the
 * server's verdict per row; `resolution_default` carries the pre-selected
 * resolution for `update` rows ('add' | 'replace', null when n/a). */
export interface ImportPreviewRow {
  row_id: string;
  kind: 'position' | 'trade';
  symbol: string | null;
  instrument_id: number | null;
  name: string | null;
  currency: string | null;
  quantity: number | null;
  price: number | null;
  date: string | null;
  confidence: number | null;
  suggestion: 'new' | 'update' | 'match' | 'needs_edit' | 'dupe';
  existing_quantity: number | null;
  qty_delta: number | null;
  reason: string | null;
  resolution_default: 'add' | 'replace' | null;
}

/** The full import review sheet, streamed via `import_preview` (spec §4.5).
 * A full-current-state snapshot: edits/undo/dismiss fold server-side through
 * the transcript, and the next `import_preview` (or REST-folded response)
 * always supersedes this one. */
export interface ImportPreview {
  import_ids: number[];
  group_id: number;
  rows: ImportPreviewRow[];
  summary: {
    positions_new: number;
    positions_update: number;
    matches: number;
    needs_edit: number;
    trades_new: number;
    trades_dupes: number;
  };
}

/** The co-authored thesis card streamed via `thesis_card` events (spec §4).
 * Full-current-state snapshots, never deltas: `symbol` is the one field the
 * backend's card coercion guarantees; every other field may be absent until
 * the user confirms it. */
export interface ThesisCard {
  symbol: string;
  duration?: 'Core' | 'Growth' | 'Speculative' | null;
  conviction_level?: number | null;
  statement?: string | null;
  pillars: string[];
  risks: string[];
  entry_price?: number | null;
  stop_loss_price?: number | null;
  take_profit_price?: number | null;
  remind_at?: string | null;
}

/** The co-authored investor profile card streamed via `profile_card` events
 * (revamp T24). Full-current-state snapshots, never deltas: every field may
 * be absent until the user confirms it; the save endpoint requires
 * style_name + target_risk_level + knowledge_level. */
export interface ProfileCard {
  style_name?: string | null;
  target_risk_level?: 'Conservative' | 'Moderate' | 'Balanced' | 'Growth' | null;
  turnover_tolerance?: string | null;
  style_description?: string | null;
  knowledge_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert' | null;
}

export interface StreamChatArgs {
  baseUrl: string;
  token: string;
  sessionId: string;
  content: string;
  /** Structured turn, sent alongside `content` (thesis builder, position
   * import, Feed-item discussion, profile builder, target-price
   * builder, or research refresh). */
  command?:
    | ThesisCommand
    | ImportCommandPayload
    | DiscussItemCommandPayload
    | BuildProfileCommandPayload
    | TargetPriceCommandPayload
    | RefreshResearchCommandPayload;
  /** The user's picks from the last question's options (multi-select steps). */
  picked?: string[];
  onDelta: (delta: string) => void;
  /**
   * A tool call finished. *durationMs* is absent when the gateway did not time
   * the call. There is deliberately no `onToolStarted`: observed calls last
   * 1–540ms, so an in-flight indicator is on screen for milliseconds at a time.
   */
  onToolCompleted?: (tool: string, durationMs?: number) => void;
  onSuggestions?: (suggestions: string[]) => void;
  onQuestion?: (question: ChatQuestion) => void;
  /** A re-emitted full-state thesis card. Like question/suggestions, always
   * lands before the terminator. */
  onThesisCard?: (card: ThesisCard) => void;
  /** A re-emitted full-state profile card (T24) — same placement doctrine as
   * onThesisCard. */
  onProfileCard?: (card: ProfileCard) => void;
  /** A re-emitted full-state target-price card (Part 2 T12) — same placement
   * doctrine: the `target_price_card` event lands before the terminator (and
   * before the question chips), so the sheet state arrives ahead of the chips. */
  onTargetPriceCard?: (card: TargetPriceCard) => void;
  /** Extraction started on an import turn: *count* files are being read.
   * Always precedes onImportPreview (which fires only when rows survive). */
  onImportPending?: (count: number, importIds: number[]) => void;
  /** The full review sheet for an import turn. Like question/thesis_card,
   * always lands before the terminator. */
  onImportPreview?: (preview: ImportPreview) => void;
  /** Abort display of an in-flight turn (e.g. the user switched sessions).
   * Aborting resolves silently: no onError, no onDone. The server-side turn
   * keeps running and persists durably. */
  signal?: AbortSignal;
  onDone: () => void;
  onError?: (err: unknown) => void;
}

export async function streamChatTurn(args: StreamChatArgs): Promise<void> {
  const {
    baseUrl,
    token,
    sessionId,
    content,
    command,
    picked,
    onDelta,
    onToolCompleted,
    onSuggestions,
    onQuestion,
    onThesisCard,
    onProfileCard,
    onTargetPriceCard,
    onImportPending,
    onImportPreview,
    signal,
    onDone,
    onError,
  } = args;
  try {
    const resp = await fetch(`${baseUrl}/v1/chat/sessions/${sessionId}/messages/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({
        content,
        ...(command ? { command } : {}),
        ...(picked ? { picked } : {}),
      }),
      signal,
    });
    if (!resp.ok) throw await toRequestError(resp);
    if (!resp.body) throw new Error('stream returned no body');
    const reader = (resp.body as any).getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop() ?? '';
      for (const part of parts) {
        const line = part.trim();
        if (!line.startsWith('data: ')) continue;
        const payload = JSON.parse(line.slice(6));
        if (payload.event === 'delta') onDelta(payload.delta ?? '');
        else if (payload.event === 'revising') {
          // ur-loop §8/R-8: the boundary rewrite started — the streamed
          // reply is about to be replaced by the reviewed version. The label
          // arrives already localized to the recipient's prefer_language:
          // this service has no i18next access, and navigator.language is the
          // device locale, not the user's.
          if (payload.label) onDelta('\n⏳ ' + payload.label);
        } else if (payload.event === 'tool.completed') {
          // The gateway reports seconds; everything downstream speaks ms.
          const durationMs =
            typeof payload.duration === 'number' ? Math.round(payload.duration * 1000) : undefined;
          onToolCompleted?.(payload.tool ?? '', durationMs);
        } else if (payload.event === 'suggestions') {
          onSuggestions?.(payload.suggestions ?? []);
        } else if (payload.event === 'question') {
          if (payload.question && Array.isArray(payload.question.options)) {
            onQuestion?.(payload.question as ChatQuestion);
          }
        } else if (payload.event === 'thesis_card') {
          // Symbol is the one field backend card coercion guarantees; without
          // it the card has no consumer downstream.
          if (payload.card && typeof payload.card.symbol === 'string') {
            onThesisCard?.(payload.card as ThesisCard);
          }
        } else if (payload.event === 'profile_card') {
          // Backend coercion already guaranteed a usable dict (T24); a bare
          // object check keeps a malformed event from reaching consumers.
          if (payload.card && typeof payload.card === 'object') {
            onProfileCard?.(payload.card as ProfileCard);
          }
        } else if (payload.event === 'target_price_card') {
          // Same guard doctrine as thesis_card: backend coercion guarantees a
          // string symbol; without it the card has no consumer downstream.
          if (payload.card && typeof payload.card.symbol === 'string') {
            onTargetPriceCard?.(payload.card as TargetPriceCard);
          }
        } else if (payload.event === 'import_pending') {
          if (typeof payload.count === 'number' && Array.isArray(payload.import_ids)) {
            onImportPending?.(payload.count, payload.import_ids as number[]);
          }
        } else if (payload.event === 'import_preview') {
          // group_id + rows are the fields every consumer needs (render, then
          // save/undo/dismiss); without them the preview has no consumer
          // downstream — same guard doctrine as the symbol-less thesis_card.
          const preview = payload.preview;
          if (preview && typeof preview.group_id === 'number' && Array.isArray(preview.rows)) {
            onImportPreview?.(preview as ImportPreview);
          }
        } else if (payload.event === 'done') {
          onDone();
          return;
        }
      }
    }
    onDone();
  } catch (err) {
    // Switching sessions aborts the display pipe; that is not an error.
    if ((err as { name?: string })?.name === 'AbortError') return;
    onError?.(err);
  }
}
